/**
 * Domain Detection Utility
 * Handles domain and subdomain detection for multi-deployment setup
 */

/**
 * Get the current domain information
 * @returns {Object} Domain information
 */
export const getCurrentDomain = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;
  
  // Build full origin
  const origin = port ? `${protocol}//${hostname}:${port}` : `${protocol}//${hostname}`;
  
  // Parse domain parts
  const parts = hostname.split('.');
  
  // Determine if it's a subdomain
  const isSubdomain = parts.length > 2;
  const subdomain = isSubdomain ? parts[0] : null;
  const rootDomain = isSubdomain ? parts.slice(1).join('.') : hostname;
  
  // Check if it's localhost or IP
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.');
  
  return {
    hostname,
    origin,
    subdomain,
    rootDomain,
    isSubdomain,
    isLocalhost,
    isBdRegion: subdomain === 'bd',
    isMainDomain: !isSubdomain || subdomain === 'www'
  };
};

/**
 * Get the canonical URL for a given path
 * Ensures URLs work correctly across different domains
 * @param {string} path - The path (e.g., '/blog/installwrf')
 * @returns {string} Full canonical URL
 */
export const getCanonicalUrl = (path) => {
  const domain = getCurrentDomain();
  
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  return `${domain.origin}${normalizedPath}`;
};

/**
 * Check if the current URL should redirect to a different domain
 * @returns {Object|null} Redirect information or null if no redirect needed
 */
export const checkDomainRedirect = () => {
  const domain = getCurrentDomain();
  
  // No redirects for localhost
  if (domain.isLocalhost) {
    return null;
  }
  
  // Add any domain-specific redirect logic here
  // For now, we don't redirect between domains
  return null;
};

/**
 * Get region-specific configuration
 * @returns {Object} Region configuration
 */
export const getRegionConfig = () => {
  const domain = getCurrentDomain();
  
  if (domain.isBdRegion) {
    return {
      region: 'bd',
      name: 'Bangladesh',
      currency: 'BDT',
      language: 'en',
      timezone: 'Asia/Dhaka'
    };
  }
  
  // Default/global configuration
  return {
    region: 'global',
    name: 'Global',
    currency: 'USD',
    language: 'en',
    timezone: 'UTC'
  };
};

/**
 * Log domain information for debugging
 */
export const logDomainInfo = () => {
  const domain = getCurrentDomain();
  const region = getRegionConfig();
  
  console.log('🌐 Domain Information:', {
    hostname: domain.hostname,
    subdomain: domain.subdomain,
    rootDomain: domain.rootDomain,
    isSubdomain: domain.isSubdomain,
    isBdRegion: domain.isBdRegion,
    region: region.region,
    fullUrl: window.location.href
  });
};

export default {
  getCurrentDomain,
  getCanonicalUrl,
  checkDomainRedirect,
  getRegionConfig,
  logDomainInfo
};
